import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Zap, History, ArrowLeft } from 'lucide-react';
import { customerAPI } from '../../services/api';

const fmtBytes = bytes => {
  const gb = (bytes || 0) / 1e9;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${((bytes || 0) / 1e6).toFixed(0)} MB`;
};
const fmtSpeed = v => (v >= 10 ? v.toFixed(0) : (v || 0).toFixed(1));
const fmtDuration = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Full broadband usage history — linked from the dashboard's Usage Stats tiles.
// Live speed, Today/Week/Month/All-Time totals, and a recent PPPoE session log.
export default function CustomerUsageDetails() {
  const navigate = useNavigate();
  const [rate, setRate] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data: r } = await customerAPI.getMyBandwidth();
        if (!cancelled && r.data) setRate(r.data);
      } catch { /* transient */ }
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    Promise.all([customerAPI.getMyUsage(), customerAPI.getMyUsageSessions()])
      .then(([u, s]) => { setUsage(u.data.data); setSessions(s.data.data || []); })
      .catch(() => toast.error('Failed to load usage details'))
      .finally(() => setLoading(false));
  }, []);

  const online = rate?.online;

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div>
      <button onClick={() => navigate(-1)} style={backBtn}>
        <ArrowLeft size={16} /> Back
      </button>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 4 }}>Usage Details</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 20 }}>Your live speed, data usage and session history</p>

      {/* Live speed */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: online ? '#22c55e' : '#ef4444' }} />
          <span style={{ fontSize: '.7rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: online ? '#16a34a' : '#dc2626' }}>
            {rate ? (online ? 'Connected' : 'Offline') : 'Checking…'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 28 }}>
          <div>
            <div style={{ fontSize: '.66rem', fontWeight: 700, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '.05em' }}>↓ Download</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmtSpeed(rate?.rx || 0)}</span>
              <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Mbps</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: '.66rem', fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '.05em' }}>↑ Upload</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmtSpeed(rate?.tx || 0)}</span>
              <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Mbps</span>
            </div>
          </div>
        </div>
      </div>

      {/* Totals */}
      {usage && (
        <>
          <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
            Data Usage
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[['Today', usage.today, '#0ea5e9'], ['This Week', usage.week, '#8b5cf6'], ['This Month', usage.month, '#f97316'], ['All Time', usage.allTime, '#059669']].map(([label, u, color]) => (
              <div key={label} className="card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Zap size={13} color={color} />
                  </div>
                  <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{fmtBytes((u?.rxBytes || 0) + (u?.txBytes || 0))}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Session history */}
      <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
        Recent Sessions
      </div>
      {!sessions || sessions.length === 0 ? (
        <div className="card empty-state" style={{ padding: 32 }}>
          <History size={28} />
          <p>No session history recorded yet.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {sessions.map((s, i) => (
            <div key={s._id || i} className="card" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{format(new Date(s.startTime), 'dd MMM yyyy, HH:mm')}</div>
                <div style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>
                  {s.endTime
                    ? `${fmtDuration((new Date(s.endTime) - new Date(s.startTime)) / 1000)} session`
                    : <span style={{ color: '#16a34a', fontWeight: 600 }}>Ongoing</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{fmtBytes((s.rxBytes || 0) + (s.txBytes || 0))}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>↓{fmtBytes(s.rxBytes)} ↑{fmtBytes(s.txBytes)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const backBtn = {
  display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-muted)', fontWeight: 600, fontSize: '.85rem', padding: 0, marginBottom: 14,
};
